import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { Can } from '../../auth/Can.js';
import type { AccountNode } from '../../types/bookkeeping.js';
import { bankingErrorMessage, flattenTree, fmtMoney } from './shared.js';

type Step = 'pick' | 'mapping' | 'preview';
type DateFormat = 'DMY' | 'MDY' | 'YMD';
type InboxStatus = 'pending' | 'excluded' | 'matched';
type PanelKind = 'match' | 'expense';

interface PreviewRow {
  date: string;
  description: string;
  amount: string;
  duplicate: boolean;
}

interface PreviewResponse {
  rows: PreviewRow[];
  errors: Array<{ line: number; message: string }>;
}

interface ImportResponse {
  batch_id: string;
  total: number;
  imported: number;
  duplicates: number;
  errors: Array<{ line: number; message: string }>;
}

interface InboxTxn {
  id: string;
  txn_date: string;
  description: string;
  amount: string;
  status: InboxStatus;
  matched_journal_entry_id: string | null;
  matched_je_date: string | null;
  matched_je_source_type: string | null;
  matched_je_memo: string | null;
  suggested_account_id: string | null;
  reconciliation_id: string | null;
}

interface Candidate {
  journal_entry_id: string;
  entry_date: string;
  memo: string | null;
  source_type: string;
}

interface MappingState {
  hasHeader: boolean;
  dateFormat: DateFormat;
  decimalComma: boolean;
  dateCol: number;
  descriptionCol: number;
  useDebitCredit: boolean;
  amountCol: number;
  debitCol: number;
  creditCol: number;
}

function defaultMapping(): MappingState {
  return {
    hasHeader: true,
    dateFormat: 'DMY',
    decimalComma: false,
    dateCol: 0,
    descriptionCol: 1,
    useDebitCredit: false,
    amountCol: 2,
    debitCol: 2,
    creditCol: 3,
  };
}

export function BankTransactionsPage() {
  const { t } = useTranslation('bookkeeping');
  const qc = useQueryClient();
  const { activeOrgId } = useAuth();

  const [accountId, setAccountId] = useState('');
  const [step, setStep] = useState<Step>('pick');
  const [filename, setFilename] = useState('');
  const [content, setContent] = useState('');
  const [format, setFormat] = useState<'csv' | 'ofx'>('csv');
  const [mapping, setMapping] = useState<MappingState>(defaultMapping());
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [importedSummary, setImportedSummary] = useState<{
    imported: number;
    duplicates: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inboxTab, setInboxTab] = useState<InboxStatus>('pending');

  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [panelKind, setPanelKind] = useState<PanelKind | null>(null);
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const [expenseAccountId, setExpenseAccountId] = useState('');
  const [expenseDescription, setExpenseDescription] = useState('');

  const { data: accountsData } = useQuery({
    queryKey: ['bookkeeping-accounts', activeOrgId],
    queryFn: () => api<{ tree: AccountNode[] }>('/bookkeeping/accounts'),
    enabled: !!activeOrgId,
  });
  const allAccounts = flattenTree(accountsData?.tree ?? []);
  const accounts = allAccounts.filter((a) => a.type === 'asset');
  const expenseAccounts = allAccounts.filter((a) => a.type === 'expense' && a.is_active);
  const accountsById = new Map(allAccounts.map((a) => [a.id, a]));

  const inboxQ = useQuery({
    queryKey: ['bk-banking-inbox', accountId, inboxTab],
    queryFn: () =>
      api<InboxTxn[]>(
        `/bookkeeping/banking/transactions?account_id=${accountId}&status=${inboxTab}`,
      ),
    enabled: !!activeOrgId && !!accountId,
  });
  const inboxRows = inboxQ.data ?? [];

  const candidatesQ = useQuery({
    queryKey: ['bk-banking-candidates', expandedRowId],
    queryFn: () =>
      api<Candidate[]>(`/bookkeeping/banking/transactions/${expandedRowId}/candidates`),
    enabled: panelKind === 'match' && !!expandedRowId,
  });
  const candidates = candidatesQ.data ?? [];

  function closePanel() {
    setExpandedRowId(null);
    setPanelKind(null);
  }

  function toggleMatchPanel(row: InboxTxn) {
    if (expandedRowId === row.id && panelKind === 'match') {
      closePanel();
      return;
    }
    setExpandedRowId(row.id);
    setPanelKind('match');
    setSelectedCandidateId(null);
  }

  function toggleExpensePanel(row: InboxTxn) {
    if (expandedRowId === row.id && panelKind === 'expense') {
      closePanel();
      return;
    }
    setExpandedRowId(row.id);
    setPanelKind('expense');
    setExpenseAccountId(row.suggested_account_id ?? '');
    setExpenseDescription(row.description);
  }

  function buildMappingPayload() {
    return {
      hasHeader: mapping.hasHeader,
      dateFormat: mapping.dateFormat,
      decimalComma: mapping.decimalComma,
      dateCol: mapping.dateCol,
      descriptionCol: mapping.descriptionCol,
      ...(mapping.useDebitCredit
        ? { debitCol: mapping.debitCol, creditCol: mapping.creditCol }
        : { amountCol: mapping.amountCol }),
    };
  }

  function resetToPick() {
    setStep('pick');
    setFilename('');
    setContent('');
    setPreview(null);
    setImportedSummary(null);
    setError(null);
  }

  const previewMut = useMutation({
    mutationFn: () =>
      api<PreviewResponse>('/bookkeeping/banking/import/preview', {
        method: 'POST',
        body: JSON.stringify({
          account_id: accountId,
          format,
          filename,
          content,
          ...(format === 'csv' ? { mapping: buildMappingPayload() } : {}),
        }),
      }),
    onSuccess: (res) => {
      setPreview(res);
      setImportedSummary(null);
      setError(null);
      setStep('preview');
    },
    onError: (e: unknown) => {
      const err = e as { body?: { error?: string }; message?: string };
      setError(bankingErrorMessage(t, err.body?.error ?? err.message));
    },
  });

  const importMut = useMutation({
    mutationFn: () =>
      api<ImportResponse>('/bookkeeping/banking/import', {
        method: 'POST',
        body: JSON.stringify({
          account_id: accountId,
          format,
          filename,
          content,
          ...(format === 'csv' ? { mapping: buildMappingPayload() } : {}),
        }),
      }),
    onSuccess: async (res) => {
      setImportedSummary({ imported: res.imported, duplicates: res.duplicates });
      setPreview(null);
      setError(null);
      setStep('pick');
      setFilename('');
      setContent('');
      await qc.invalidateQueries({ queryKey: ['bk-banking-inbox', accountId] });
    },
    onError: (e: unknown) => {
      const err = e as { body?: { error?: string }; message?: string };
      setError(bankingErrorMessage(t, err.body?.error ?? err.message));
    },
  });

  const excludeMut = useMutation({
    mutationFn: (id: string) =>
      api(`/bookkeeping/banking/transactions/${id}/exclude`, { method: 'POST' }),
    onSuccess: () => {
      setError(null);
      return qc.invalidateQueries({ queryKey: ['bk-banking-inbox', accountId] });
    },
    onError: (e: unknown) => {
      const err = e as { body?: { error?: string }; message?: string };
      setError(bankingErrorMessage(t, err.body?.error ?? err.message));
    },
  });
  const restoreMut = useMutation({
    mutationFn: (id: string) =>
      api(`/bookkeeping/banking/transactions/${id}/restore`, { method: 'POST' }),
    onSuccess: () => {
      setError(null);
      return qc.invalidateQueries({ queryKey: ['bk-banking-inbox', accountId] });
    },
    onError: (e: unknown) => {
      const err = e as { body?: { error?: string }; message?: string };
      setError(bankingErrorMessage(t, err.body?.error ?? err.message));
    },
  });

  const matchMut = useMutation({
    mutationFn: (vars: { id: string; journal_entry_id: string }) =>
      api(`/bookkeeping/banking/transactions/${vars.id}/match`, {
        method: 'POST',
        body: JSON.stringify({ journal_entry_id: vars.journal_entry_id }),
      }),
    onSuccess: async () => {
      closePanel();
      setError(null);
      await qc.invalidateQueries({ queryKey: ['bk-banking-inbox', accountId] });
    },
    onError: (e: unknown) => {
      const err = e as { body?: { error?: string }; message?: string };
      setError(bankingErrorMessage(t, err.body?.error ?? err.message));
    },
  });

  const unmatchMut = useMutation({
    mutationFn: (id: string) =>
      api(`/bookkeeping/banking/transactions/${id}/unmatch`, { method: 'POST' }),
    onSuccess: () => {
      setError(null);
      return qc.invalidateQueries({ queryKey: ['bk-banking-inbox', accountId] });
    },
    onError: (e: unknown) => {
      const err = e as { body?: { error?: string }; message?: string };
      setError(bankingErrorMessage(t, err.body?.error ?? err.message));
    },
  });

  const createExpenseMut = useMutation({
    mutationFn: (vars: { id: string; account_id: string; description: string }) =>
      api(`/bookkeeping/banking/transactions/${vars.id}/create-expense`, {
        method: 'POST',
        body: JSON.stringify({ account_id: vars.account_id, description: vars.description }),
      }),
    onSuccess: async () => {
      closePanel();
      setError(null);
      await qc.invalidateQueries({ queryKey: ['bk-banking-inbox', accountId] });
    },
    onError: (e: unknown) => {
      const err = e as { body?: { error?: string }; message?: string };
      setError(bankingErrorMessage(t, err.body?.error ?? err.message));
    },
  });

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setImportedSummary(null);
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      const lower = file.name.toLowerCase();
      const fmt: 'csv' | 'ofx' = lower.endsWith('.ofx') || lower.endsWith('.qfx') ? 'ofx' : 'csv';
      setFilename(file.name);
      setContent(text);
      setFormat(fmt);
      if (fmt === 'ofx') {
        // Fire the preview fetch directly using the just-read values (state
        // hasn't flushed yet, so build the payload here rather than relying
        // on component state).
        api<PreviewResponse>('/bookkeeping/banking/import/preview', {
          method: 'POST',
          body: JSON.stringify({
            account_id: accountId,
            format: fmt,
            filename: file.name,
            content: text,
          }),
        })
          .then((res) => {
            setPreview(res);
            setStep('preview');
          })
          .catch((err: unknown) => {
            const e2 = err as { body?: { error?: string }; message?: string };
            setError(bankingErrorMessage(t, e2.body?.error ?? e2.message));
          });
      } else {
        setStep('mapping');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  }

  const firstLines = content
    .split(/\r?\n/)
    .filter((l) => l.length > 0)
    .slice(0, 5)
    .map((l) => l.split(','));
  const maxCols = firstLines[0]?.length ?? 0;
  const colIndexes = Array.from({ length: maxCols }, (_, i) => i);

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">{t('banking.title')}</h1>

      <h2 className="text-sm font-semibold text-slate-600">{t('banking.upload')}</h2>
      <div className="grid grid-cols-2 gap-3 rounded border bg-white p-4 text-sm">
        <label>
          {t('banking.account')}
          <select
            value={accountId}
            onChange={(e) => {
              setAccountId(e.target.value);
              setStep('pick');
              setFilename('');
              setContent('');
              setPreview(null);
              setError(null);
              closePanel();
            }}
            className="mt-1 block w-full rounded border px-2 py-1"
          >
            <option value="">—</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.code} {a.name}
              </option>
            ))}
          </select>
        </label>
        {step === 'pick' && (
          <label>
            {t('banking.file')}
            <input
              type="file"
              accept=".csv,.ofx,.qfx"
              disabled={!accountId}
              onChange={handleFile}
              className="mt-1 block w-full text-sm"
            />
          </label>
        )}
      </div>

      {importedSummary && (
        <p className="rounded border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-800">
          {t('banking.imported_summary', {
            imported: importedSummary.imported,
            duplicates: importedSummary.duplicates,
          })}
        </p>
      )}
      {error && (
        <p className="rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </p>
      )}

      {step === 'mapping' && (
        <div className="space-y-3 rounded border bg-white p-4 text-sm">
          <h2 className="font-semibold">{t('banking.step_mapping')}</h2>

          <div className="overflow-x-auto rounded border">
            <table className="w-full text-xs">
              <thead className="bg-slate-50">
                <tr>
                  {colIndexes.map((i) => (
                    <th key={i} className="px-2 py-1 text-left">
                      {i}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {firstLines.map((cols, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: stable raw preview order
                  <tr key={i} className="border-t">
                    {cols.map((cell, j) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: stable column order
                      <td key={j} className="px-2 py-1">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={mapping.hasHeader}
                onChange={(e) => setMapping((m) => ({ ...m, hasHeader: e.target.checked }))}
              />
              {t('banking.has_header')}
            </label>
            <label>
              {t('banking.date_format')}
              <select
                value={mapping.dateFormat}
                onChange={(e) =>
                  setMapping((m) => ({ ...m, dateFormat: e.target.value as DateFormat }))
                }
                className="mt-1 block w-full rounded border px-2 py-1"
              >
                <option value="DMY">DMY</option>
                <option value="MDY">MDY</option>
                <option value="YMD">YMD</option>
              </select>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={mapping.decimalComma}
                onChange={(e) => setMapping((m) => ({ ...m, decimalComma: e.target.checked }))}
              />
              {t('banking.decimal_comma')}
            </label>

            <label>
              {t('banking.col_date')}
              <select
                value={mapping.dateCol}
                onChange={(e) => setMapping((m) => ({ ...m, dateCol: Number(e.target.value) }))}
                className="mt-1 block w-full rounded border px-2 py-1"
              >
                {colIndexes.map((i) => (
                  <option key={i} value={i}>
                    {i}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {t('banking.col_description')}
              <select
                value={mapping.descriptionCol}
                onChange={(e) =>
                  setMapping((m) => ({ ...m, descriptionCol: Number(e.target.value) }))
                }
                className="mt-1 block w-full rounded border px-2 py-1"
              >
                {colIndexes.map((i) => (
                  <option key={i} value={i}>
                    {i}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={mapping.useDebitCredit}
                onChange={(e) => setMapping((m) => ({ ...m, useDebitCredit: e.target.checked }))}
              />
              {t('banking.use_debit_credit')}
            </label>

            {mapping.useDebitCredit ? (
              <>
                <label>
                  {t('banking.col_debit')}
                  <select
                    value={mapping.debitCol}
                    onChange={(e) =>
                      setMapping((m) => ({ ...m, debitCol: Number(e.target.value) }))
                    }
                    className="mt-1 block w-full rounded border px-2 py-1"
                  >
                    {colIndexes.map((i) => (
                      <option key={i} value={i}>
                        {i}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  {t('banking.col_credit')}
                  <select
                    value={mapping.creditCol}
                    onChange={(e) =>
                      setMapping((m) => ({ ...m, creditCol: Number(e.target.value) }))
                    }
                    className="mt-1 block w-full rounded border px-2 py-1"
                  >
                    {colIndexes.map((i) => (
                      <option key={i} value={i}>
                        {i}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            ) : (
              <label>
                {t('banking.col_amount')}
                <select
                  value={mapping.amountCol}
                  onChange={(e) => setMapping((m) => ({ ...m, amountCol: Number(e.target.value) }))}
                  className="mt-1 block w-full rounded border px-2 py-1"
                >
                  {colIndexes.map((i) => (
                    <option key={i} value={i}>
                      {i}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => previewMut.mutate()}
              disabled={previewMut.isPending}
              className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:bg-slate-300"
            >
              {t('banking.preview')}
            </button>
            <button
              type="button"
              onClick={() => resetToPick()}
              className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              {t('banking.change_file')}
            </button>
          </div>
        </div>
      )}

      {step === 'preview' && preview && (
        <div className="space-y-3 rounded border bg-white p-4 text-sm">
          <div className="overflow-x-auto rounded border">
            <table className="w-full text-sm">
              <thead className="border-b bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2">{t('banking.date')}</th>
                  <th className="px-3 py-2">{t('banking.description')}</th>
                  <th className="px-3 py-2 text-right">{t('banking.amount')}</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {preview.rows.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-3 py-4 text-center text-slate-500">
                      {t('banking.no_rows')}
                    </td>
                  </tr>
                )}
                {preview.rows.map((row, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: preview rows have no stable id
                  <tr key={i} className="border-t">
                    <td className="px-3 py-2">{row.date}</td>
                    <td className="px-3 py-2">{row.description}</td>
                    <td
                      className={`px-3 py-2 text-right font-mono ${
                        row.amount.startsWith('-') ? 'text-red-600' : 'text-green-600'
                      }`}
                    >
                      {fmtMoney(row.amount)}
                    </td>
                    <td className="px-3 py-2">
                      {row.duplicate && (
                        <span className="rounded bg-yellow-100 px-2 py-0.5 text-xs text-yellow-800">
                          {t('banking.duplicate')}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {preview.errors.length > 0 && (
            <div className="rounded border border-red-200 bg-red-50 p-3 text-xs text-red-700">
              <p className="mb-1 font-semibold">{t('banking.errors_title')}</p>
              <ul className="list-inside list-disc">
                {preview.errors.map((err) => (
                  <li key={err.line}>
                    {err.line}: {err.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex gap-2">
            <Can I="manage" a="Account">
              <button
                type="button"
                onClick={() => importMut.mutate()}
                disabled={importMut.isPending}
                className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:bg-slate-300"
              >
                {t('banking.import')}
              </button>
            </Can>
            <button
              type="button"
              onClick={() => resetToPick()}
              className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              {t('banking.change_file')}
            </button>
          </div>
        </div>
      )}

      {accountId && (
        <div className="rounded border bg-white p-4">
          <div className="mb-3 flex gap-2 text-sm">
            <button
              type="button"
              onClick={() => {
                setInboxTab('pending');
                closePanel();
              }}
              className={`rounded px-3 py-1.5 ${
                inboxTab === 'pending' ? 'bg-blue-100 text-blue-900' : 'text-slate-600'
              }`}
            >
              {t('banking.pending')}
            </button>
            <button
              type="button"
              onClick={() => {
                setInboxTab('matched');
                closePanel();
              }}
              className={`rounded px-3 py-1.5 ${
                inboxTab === 'matched' ? 'bg-blue-100 text-blue-900' : 'text-slate-600'
              }`}
            >
              {t('banking.matched')}
            </button>
            <button
              type="button"
              onClick={() => {
                setInboxTab('excluded');
                closePanel();
              }}
              className={`rounded px-3 py-1.5 ${
                inboxTab === 'excluded' ? 'bg-blue-100 text-blue-900' : 'text-slate-600'
              }`}
            >
              {t('banking.excluded')}
            </button>
          </div>
          <div className="overflow-x-auto rounded border">
            <table className="w-full text-sm">
              <thead className="border-b bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2">{t('banking.date')}</th>
                  <th className="px-3 py-2">{t('banking.description')}</th>
                  <th className="px-3 py-2 text-right">{t('banking.amount')}</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {inboxRows.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-3 py-4 text-center text-slate-500">
                      {t('banking.no_rows')}
                    </td>
                  </tr>
                )}
                {inboxRows.map((row) => {
                  const suggestedAccount = row.suggested_account_id
                    ? accountsById.get(row.suggested_account_id)
                    : undefined;
                  const isExpanded = expandedRowId === row.id;
                  return (
                    <Fragment key={row.id}>
                      <tr className="border-t">
                        <td className="px-3 py-2">{row.txn_date}</td>
                        <td className="px-3 py-2">
                          {row.description}
                          {inboxTab === 'pending' && suggestedAccount && (
                            <span className="ml-2 rounded bg-blue-50 px-2 py-0.5 text-xs text-blue-700">
                              {t('banking.suggested')}: {suggestedAccount.code}
                            </span>
                          )}
                        </td>
                        <td
                          className={`px-3 py-2 text-right font-mono ${
                            row.amount.startsWith('-') ? 'text-red-600' : 'text-green-600'
                          }`}
                        >
                          {fmtMoney(row.amount)}
                        </td>
                        <td className="px-3 py-2">
                          {inboxTab === 'pending' && (
                            <div className="flex gap-3">
                              <Can I="manage" a="Account">
                                <button
                                  type="button"
                                  onClick={() => toggleMatchPanel(row)}
                                  className="text-blue-600 hover:underline"
                                >
                                  {t('banking.match')}
                                </button>
                              </Can>
                              {row.amount.startsWith('-') && (
                                <Can I="manage" a="Account">
                                  <button
                                    type="button"
                                    onClick={() => toggleExpensePanel(row)}
                                    className="text-blue-600 hover:underline"
                                  >
                                    {t('banking.create_expense')}
                                  </button>
                                </Can>
                              )}
                              <Can I="manage" a="Account">
                                <button
                                  type="button"
                                  onClick={() => excludeMut.mutate(row.id)}
                                  className="text-red-600 hover:underline"
                                >
                                  {t('banking.exclude')}
                                </button>
                              </Can>
                            </div>
                          )}
                          {inboxTab === 'excluded' && (
                            <Can I="manage" a="Account">
                              <button
                                type="button"
                                onClick={() => restoreMut.mutate(row.id)}
                                className="text-blue-600 hover:underline"
                              >
                                {t('banking.restore')}
                              </button>
                            </Can>
                          )}
                          {inboxTab === 'matched' && (
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-slate-500">
                                {t('banking.matched_to')}:{' '}
                                {row.matched_je_date && row.matched_je_source_type
                                  ? `${row.matched_je_date} · ${row.matched_je_source_type} · ${row.matched_je_memo ?? ''}`
                                  : row.matched_journal_entry_id}
                              </span>
                              {row.reconciliation_id ? (
                                <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                                  {t('banking.reconciled_lock')}
                                </span>
                              ) : (
                                <Can I="manage" a="Account">
                                  <button
                                    type="button"
                                    onClick={() => unmatchMut.mutate(row.id)}
                                    className="text-red-600 hover:underline"
                                  >
                                    {t('banking.unmatch')}
                                  </button>
                                </Can>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                      {isExpanded && panelKind === 'match' && (
                        <tr className="border-t bg-slate-50">
                          <td colSpan={4} className="px-3 py-3">
                            <p className="mb-2 text-xs font-semibold text-slate-600">
                              {t('banking.candidates_title')}
                            </p>
                            {candidates.length === 0 ? (
                              <p className="text-sm text-slate-500">{t('banking.no_candidates')}</p>
                            ) : (
                              <ul className="mb-3 space-y-1">
                                {candidates.map((cand) => (
                                  <li key={cand.journal_entry_id}>
                                    <label className="flex items-center gap-2 text-sm">
                                      <input
                                        type="radio"
                                        name={`candidate-${row.id}`}
                                        checked={selectedCandidateId === cand.journal_entry_id}
                                        onChange={() =>
                                          setSelectedCandidateId(cand.journal_entry_id)
                                        }
                                      />
                                      <span>
                                        {cand.entry_date} · {cand.source_type} · {cand.memo ?? ''}
                                      </span>
                                    </label>
                                  </li>
                                ))}
                              </ul>
                            )}
                            <div className="flex gap-2">
                              <Can I="manage" a="Account">
                                <button
                                  type="button"
                                  disabled={!selectedCandidateId || matchMut.isPending}
                                  onClick={() =>
                                    selectedCandidateId &&
                                    matchMut.mutate({
                                      id: row.id,
                                      journal_entry_id: selectedCandidateId,
                                    })
                                  }
                                  className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:bg-slate-300"
                                >
                                  {t('banking.confirm')}
                                </button>
                              </Can>
                              <button
                                type="button"
                                onClick={() => closePanel()}
                                className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                              >
                                {t('banking.cancel')}
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                      {isExpanded && panelKind === 'expense' && (
                        <tr className="border-t bg-slate-50">
                          <td colSpan={4} className="px-3 py-3">
                            <div className="mb-3 grid grid-cols-2 gap-3 text-sm">
                              <label>
                                {t('banking.category')}
                                <select
                                  value={expenseAccountId}
                                  onChange={(e) => setExpenseAccountId(e.target.value)}
                                  className="mt-1 block w-full rounded border px-2 py-1"
                                >
                                  <option value="">—</option>
                                  {expenseAccounts.map((a) => (
                                    <option key={a.id} value={a.id}>
                                      {a.code} {a.name}
                                    </option>
                                  ))}
                                </select>
                              </label>
                              <label>
                                {t('banking.description')}
                                <input
                                  type="text"
                                  value={expenseDescription}
                                  onChange={(e) => setExpenseDescription(e.target.value)}
                                  className="mt-1 block w-full rounded border px-2 py-1"
                                />
                              </label>
                            </div>
                            <div className="flex gap-2">
                              <Can I="manage" a="Account">
                                <button
                                  type="button"
                                  disabled={!expenseAccountId || createExpenseMut.isPending}
                                  onClick={() =>
                                    createExpenseMut.mutate({
                                      id: row.id,
                                      account_id: expenseAccountId,
                                      description: expenseDescription,
                                    })
                                  }
                                  className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:bg-slate-300"
                                >
                                  {t('banking.confirm')}
                                </button>
                              </Can>
                              <button
                                type="button"
                                onClick={() => closePanel()}
                                className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                              >
                                {t('banking.cancel')}
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
